import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';

// Types
export interface EmailTemplate {
  id: string;
  name: string;
  description: string | null;
  subject: string;
  content: string;
  previewText: string | null;
  headerImage: string | null;
  primaryColor: string;
  secondaryColor: string;
  footerText: string | null;
  category: 'MARKETING' | 'TRANSACTIONAL' | 'NOTIFICATION';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCampaign {
  id: string;
  tenantId: string | null;
  name: string;
  subject: string;
  content: string;
  previewText: string | null;
  targetType: 'ALL_LEADS' | 'SELECTED_LEADS' | 'FILTERED_LEADS' | 'ALL_INSTITUTIONS' | 'SPECIFIC_INSTITUTIONS' | 'SEGMENT' | 'LEADS';
  targetIds: string[];
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED';
  scheduledAt: string | null;
  sentAt: string | null;
  isAutomated: boolean;
  triggerType: 'WELCOME' | 'TRIAL_ENDING' | 'ABANDONED_SIGNUP' | 'RE_ENGAGEMENT' | 'DISCOUNT_OFFER' | 'PAYMENT_REMINDER' | 'SUBSCRIPTION_RENEWED' | 'LEAD_ADDED' | 'LEAD_INACTIVE' | 'FORMER_USER_ADDED' | null;
  triggerDelay: number | null;
  templateId: string | null;
  template?: EmailTemplate;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    sends: number;
  };
}

export interface EmailAutomation {
  id: string;
  name: string;
  description?: string | null;
  triggerType: 'WELCOME' | 'TRIAL_ENDING' | 'ABANDONED_SIGNUP' | 'RE_ENGAGEMENT' | 'DISCOUNT_OFFER' | 'PAYMENT_REMINDER' | 'SUBSCRIPTION_RENEWED' | 'LEAD_ADDED' | 'LEAD_INACTIVE' | 'FORMER_USER_ADDED';
  triggerDelay: number;
  triggerConfig?: Record<string, unknown> | null;
  templateId: string | null;
  template?: EmailTemplate;
  subject: string | null;
  content: string | null;
  isActive: boolean;
  totalTriggered?: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailUnsubscribe {
  id: string;
  email: string;
  reason: string | null;
  unsubscribedAt: string;
}

export interface CreateTemplateDto {
  name: string;
  description?: string;
  subject: string;
  content: string;
  previewText?: string;
  headerImage?: string;
  primaryColor?: string;
  secondaryColor?: string;
  footerText?: string;
  category: 'MARKETING' | 'TRANSACTIONAL' | 'NOTIFICATION';
}

export interface CreateCampaignDto {
  name: string;
  subject: string;
  content: string;
  previewText?: string;
  targetType: 'ALL_LEADS' | 'SELECTED_LEADS' | 'FILTERED_LEADS' | 'ALL_INSTITUTIONS' | 'SPECIFIC_INSTITUTIONS' | 'SEGMENT' | 'LEADS';
  targetIds?: string[];
  templateId?: string;
}

export interface CreateAutomationDto {
  name: string;
  description?: string;
  triggerType: 'WELCOME' | 'TRIAL_ENDING' | 'ABANDONED_SIGNUP' | 'RE_ENGAGEMENT' | 'DISCOUNT_OFFER' | 'PAYMENT_REMINDER' | 'SUBSCRIPTION_RENEWED' | 'LEAD_ADDED' | 'LEAD_INACTIVE' | 'FORMER_USER_ADDED';
  triggerDelay: number;
  triggerConfig?: Record<string, unknown>;
  templateId?: string;
  subject?: string;
  content?: string;
}

// API Functions - Templates
export const getTemplates = async (category?: string): Promise<EmailTemplate[]> => {
  const query = category ? `?category=${category}` : '';
  return apiGet<EmailTemplate[]>(`/email/templates${query}`);
};

export const getTemplate = async (id: string): Promise<EmailTemplate> => {
  return apiGet<EmailTemplate>(`/email/templates/${id}`);
};

export const createTemplate = async (data: CreateTemplateDto): Promise<EmailTemplate> => {
  return apiPost<EmailTemplate>('/email/templates', data);
};

export const updateTemplate = async (id: string, data: Partial<CreateTemplateDto>): Promise<EmailTemplate> => {
  return apiPatch<EmailTemplate>(`/email/templates/${id}`, data);
};

export const deleteTemplate = async (id: string): Promise<void> => {
  return apiDelete(`/email/templates/${id}`);
};

// Paginated response type
interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// API Functions - Campaigns
export const getCampaigns = async (status?: string): Promise<EmailCampaign[]> => {
  const query = status ? `?status=${status}` : '';
  const response = await apiGet<PaginatedResponse<EmailCampaign> | EmailCampaign[]>(`/email/campaigns${query}`);
  // Handle both paginated and array responses
  return Array.isArray(response) ? response : response.data;
};

export const getCampaign = async (id: string): Promise<EmailCampaign> => {
  return apiGet<EmailCampaign>(`/email/campaigns/${id}`);
};

export const createCampaign = async (data: CreateCampaignDto): Promise<EmailCampaign> => {
  return apiPost<EmailCampaign>('/email/campaigns', data);
};

export const updateCampaign = async (id: string, data: Partial<CreateCampaignDto>): Promise<EmailCampaign> => {
  return apiPatch<EmailCampaign>(`/email/campaigns/${id}`, data);
};

export const scheduleCampaign = async (id: string, scheduledAt: string): Promise<EmailCampaign> => {
  return apiPost<EmailCampaign>(`/email/campaigns/${id}/schedule`, { scheduledAt });
};

export const sendCampaign = async (id: string): Promise<EmailCampaign> => {
  return apiPost<EmailCampaign>(`/email/campaigns/${id}/send`, {});
};

export const cancelCampaign = async (id: string): Promise<EmailCampaign> => {
  return apiPost<EmailCampaign>(`/email/campaigns/${id}/cancel`, {});
};

export const deleteCampaign = async (id: string): Promise<void> => {
  return apiDelete(`/email/campaigns/${id}`);
};

// API Functions - Automations
export const getAutomations = async (): Promise<EmailAutomation[]> => {
  return apiGet<EmailAutomation[]>('/email/automations');
};

export const createAutomation = async (data: CreateAutomationDto): Promise<EmailAutomation> => {
  return apiPost<EmailAutomation>('/email/automations', data);
};

export const updateAutomation = async (id: string, data: Partial<CreateAutomationDto & { isActive: boolean }>): Promise<EmailAutomation> => {
  return apiPatch<EmailAutomation>(`/email/automations/${id}`, data);
};

// Helpers
export const getCampaignStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    SCHEDULED: 'bg-blue-100 text-blue-800',
    SENDING: 'bg-yellow-100 text-yellow-800',
    SENT: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getCampaignStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    DRAFT: 'Brouillon',
    SCHEDULED: 'Planifiée',
    SENDING: 'En cours',
    SENT: 'Envoyée',
    CANCELLED: 'Annulée',
  };
  return labels[status] || status;
};

export const getTriggerLabel = (trigger: string): string => {
  const labels: Record<string, string> = {
    LEAD_ADDED: 'Nouveau lead ajouté',
    FORMER_USER_ADDED: 'Ancien utilisateur ajouté',
    LEAD_INACTIVE: 'Lead inactif',
    PAYMENT_REMINDER: 'Rappel de paiement',
    TRIAL_ENDING: 'Fin d\'essai',
    WELCOME: 'Bienvenue',
    ABANDONED_SIGNUP: 'Inscription abandonnée',
    RE_ENGAGEMENT: 'Réengagement',
    DISCOUNT_OFFER: 'Offre promotionnelle',
    SUBSCRIPTION_RENEWED: 'Renouvellement',
  };
  return labels[trigger] || trigger;
};

// Triggers that the v1 backend actually fires; the rest are reserved for later
export const ACTIVE_TRIGGERS: string[] = [
  'LEAD_ADDED',
  'FORMER_USER_ADDED',
];
// "Coming soon" triggers — visible but disabled in the form
export const COMING_SOON_TRIGGERS: string[] = [
  'LEAD_INACTIVE',
  'PAYMENT_REMINDER',
  'TRIAL_ENDING',
  'WELCOME',
  'ABANDONED_SIGNUP',
  'RE_ENGAGEMENT',
  'DISCOUNT_OFFER',
  'SUBSCRIPTION_RENEWED',
];

export const getCategoryLabel = (category: string): string => {
  const labels: Record<string, string> = {
    MARKETING: 'Marketing',
    TRANSACTIONAL: 'Transactionnel',
    NOTIFICATION: 'Notification',
  };
  return labels[category] || category;
};

export const getTargetTypeLabel = (targetType: string): string => {
  const labels: Record<string, string> = {
    ALL_LEADS: 'Tous les leads',
    SELECTED_LEADS: 'Leads sélectionnés',
    FILTERED_LEADS: 'Leads filtrés',
    ALL_INSTITUTIONS: 'Tous les établissements (legacy)',
    SPECIFIC_INSTITUTIONS: 'Établissements spécifiques (legacy)',
    SEGMENT: 'Segment (legacy)',
    LEADS: 'Prospects (legacy)',
  };
  return labels[targetType] || targetType;
};

// ---- Variables (for the template editor) ----
export interface TemplateVariable {
  key: string;     // {{key}}
  label: string;
  sample: string;
  group: 'lead' | 'custom' | 'system';
}

export const listTemplateVariables = (): Promise<TemplateVariable[]> =>
  apiGet<TemplateVariable[]>('/email/variables');

// Pure JS version of the backend renderer (used for live preview).
// Mirrors backend/src/modules/email-crm/template-renderer.ts.
export interface RenderContextLead {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  phone?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  city?: string | null;
  country?: string | null;
  customFields?: Record<string, unknown> | null;
}

export const renderTemplate = (
  input: string | null | undefined,
  ctx: { lead: RenderContextLead; unsubscribeUrl?: string; senderName?: string },
): string => {
  if (!input) return '';
  return input.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_m, raw: string) => {
    const key = raw.trim();
    if (key === 'unsubscribeUrl') return ctx.unsubscribeUrl ?? '#';
    if (key === 'senderName') return ctx.senderName ?? 'NEXEDU';
    if (key.startsWith('customFields.')) {
      const sub = key.slice('customFields.'.length);
      const v = (ctx.lead.customFields ?? {})[sub];
      return v == null ? '' : String(v);
    }
    const v = (ctx.lead as unknown as Record<string, unknown>)[key];
    return v == null ? '' : String(v);
  });
};
